import type { ReactNode } from 'react';

import { General, Shapes } from '../StrokeIcons';
import Button from './Button';
import type { Size } from './types';

export type CheckboxButtonProps = {
  /** Current setting. Drives the glyph, the fill, and `aria-pressed`. */
  checked: boolean;
  /** The label. Kept identical in both states so the control reads as a setting, not an action. */
  children: ReactNode;
  onChange: (checked: boolean) => void;
  /** State-specific copy — the label alone cannot say which way the option is set. */
  tooltip?: ReactNode;
  size?: Size;
  disabled?: boolean;
};

/**
 * A labelled on/off chip: checkbox glyph + text, filled when on.
 *
 * The labelled sibling of {@link CheckboxIconButton} (same `CheckSquare`/`Square` glyph pair, same
 * blue-on / gray-off axis), for rows of boolean options that need words rather than an icon —
 * e.g. the step editor's "Use Email Signature" and "Reply in thread".
 *
 * Why the glyph rather than fill alone: a lone chip gives the reader nothing to compare against, so
 * "off" and "a button I have not pressed yet" look identical — colour only reads as state when both
 * states happen to be on screen at once, which they usually are not. The glyph states the setting on
 * its own, and `aria-pressed` says the same thing to a screen reader.
 */
export default function CheckboxButton({
  checked,
  children,
  onChange,
  tooltip,
  size = 'sm',
  disabled,
}: CheckboxButtonProps) {
  return (
    <Button
      size={size}
      color={checked ? 'blue' : 'gray'}
      aria-pressed={checked}
      disabled={disabled}
      tooltip={tooltip}
      icon={checked ? <General.CheckSquare aria-hidden="true" /> : <Shapes.Square aria-hidden="true" />}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onChange(!checked);
      }}>
      {children}
    </Button>
  );
}
